/**
 * What actually changed between two spec versions.
 *
 * `/versions` already returns every field of _VERSIONED_FIELDS for every
 * version, so the diff needs nothing from the server — the tab was showing a
 * change summary somebody typed and nothing about what the change did.
 *
 * ── The off-by-one, which is the whole reason this file has a comment ──
 *
 * commit_version() writes row vN holding the spec as it stood *before* the
 * change, and stamps that row with the summary of the change about to happen.
 * So row v16 does not contain "the sixteen-section CRUX" that its own summary
 * describes — it contains the CRUX from before the rewrite. The result of that
 * change lives in row v17, and for the newest row it lives on the app itself.
 *
 *     diff for the change described on row vN  =  row(vN) → row(vN+1)
 *
 * Reading it the other way round shows every change one version late, which is
 * indistinguishable from correct until you check a diff against what you know
 * you edited.
 */
import { CRUX_SECTIONS } from '../constants/cruxSections';

/* Ordered as they are read, not as _VERSIONED_FIELDS declares them: the two
 * that carry the meaning of a change come first, and the URLs somebody fixed a
 * typo in come last. A field missing here still diffs — it falls back to its
 * column name — so adding one to the snapshot cannot make it invisible. */
export const FIELD_ORDER = [
  'crux', 'data_lender_request',
  'name', 'description', 'deployment_mode', 'app_store_visibility', 'is_active',
  'redirect_uris', 'webhook_url', 'webhook_events', 'automation_endpoint_url',
  'enc_public_key', 'plugins_enabled', 'allowed_user_ids',
  'website_url', 'repository_url', 'privacy_policy_url', 'terms_of_service_url',
  'developer_email',
  'execution_type', 'plugin_endpoint_url', 'plugin_endpoint_base_url',
  'mcp_server_url', 'tools', 'output_declaration', 'usage_guide',
  'requires_consent', 'remove_source', 'input_schema_id', 'output_schema_id',
  'input_parameters',
];

export const FIELD_LABELS = {
  crux: 'CRUX',
  data_lender_request: 'Access',
  pricing: 'Price',
  name: 'Name',
  description: 'Description',
  deployment_mode: 'Deployment',
  app_store_visibility: 'Store visibility',
  is_active: 'Active',
  redirect_uris: 'Redirect URIs',
  webhook_url: 'Webhook URL',
  webhook_events: 'Webhook events',
  automation_endpoint_url: 'Automation endpoint',
  enc_public_key: 'Encryption key',
  plugins_enabled: 'Plugins enabled',
  allowed_user_ids: 'Allowed users',
  website_url: 'Website',
  repository_url: 'Repository',
  privacy_policy_url: 'Privacy policy',
  terms_of_service_url: 'Terms of service',
  developer_email: 'Developer email',
  execution_type: 'Execution type',
  plugin_endpoint_url: 'Plugin endpoint',
  plugin_endpoint_base_url: 'Plugin endpoint base',
  mcp_server_url: 'MCP server',
  // Retired; older version snapshots still carry it.
  native_fn: 'Native function',
  tools: 'Tools',
  output_declaration: 'Output declaration',
  usage_guide: 'Usage guide',
  requires_consent: 'Requires consent',
  remove_source: 'Removes source',
  input_schema_id: 'Input schema',
  output_schema_id: 'Output schema',
  input_parameters: 'Input parameters',
};

const SECTION_LABEL = new Map(CRUX_SECTIONS.map(s => [s.pattern, s.label]));

/** A heading key nobody recognises still needs a name a person can read. */
function sectionLabel(pattern) {
  return SECTION_LABEL.get(pattern)
    || String(pattern).replace(/^\^?#+\\?\.?\s*/, '').replace(/\\/g, '').replace(/^\^/, '')
    || 'Untitled section';
}

function canonical(v) {
  if (v === null || typeof v !== 'object') return JSON.stringify(v ?? null);
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  return `{${Object.keys(v).sort().map(k => `${JSON.stringify(k)}:${canonical(v[k])}`).join(',')}}`;
}

/* The many ways a field says "not set". A version row stores the column, and the
 * live app runs some of them through `or None` or `get_tools()` on the way out —
 * so the newest change diffs null against [] on fields nobody touched, and every
 * one of those reads as an edit. Empty is empty; a real clear still shows,
 * because only one side of it is empty. */
const isEmpty = v => v == null || v === ''
  || (Array.isArray(v) && v.length === 0)
  || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

/** As canonical(), but list order carries no meaning either. */
function canonicalLoose(v) {
  if (v === null || typeof v !== 'object') return JSON.stringify(v ?? null);
  if (Array.isArray(v)) return `[${v.map(canonicalLoose).sort().join(',')}]`;
  return `{${Object.keys(v).sort().map(k => `${JSON.stringify(k)}:${canonicalLoose(v[k])}`).join(',')}}`;
}

/** Stable, order-insensitive comparison — JSONB comes back with no key order. */
function same(a, b) {
  if (a === b) return true;
  if (isEmpty(a) && isEmpty(b)) return true;
  if (a == null || b == null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return a === b;
  return canonical(a) === canonical(b);
}

const isBlank = v => v == null || (typeof v === 'string' && !v.trim());

// ── the two fields that carry a change's meaning ────────────────────────────

/**
 * A permission is a resource:verb; the schemas are which data it reaches.
 *
 * Read from `dlr_inputs`, which is the canonical itemised request list — see
 * normalize_inputs in backend/app/authz/dlr.py. `requests` is the key the YAML
 * serialisation uses, and reading it instead found nothing on a real app: two
 * changes whose own summary said "Updated access request" reported no access
 * change at all. `requests` is still checked, for a DLR authored as YAML.
 *
 * Items with no resource and no verb are plugin-IO wiring, not permissions, and
 * every access consumer ignores them. So does this.
 *
 * Keyed rather than compared positionally: reordering the list is not a
 * permission change, and a diff that says it is trains people to stop reading
 * the one screen where an added `nodes:delete` needs to be noticed.
 */
function permissions(dlr) {
  const items = [...(dlr?.dlr_inputs || []), ...(dlr?.requests || [])];
  const out = new Map();
  for (const r of items) {
    if (!r || typeof r !== 'object') continue;
    if (!r.resource || !r.verb) continue;
    const key = `${r.resource}:${r.verb}`;
    const prev = out.get(key) || { schemas: [], scope: null };
    // The three field-name dialects the normaliser unifies, in one place.
    const raw = r.schema_ids || r.schemas || (r.schema ? [r.schema] : []);
    for (const s of (Array.isArray(raw) ? raw : [raw])) {
      if (s && !prev.schemas.includes(String(s))) prev.schemas.push(String(s));
    }
    // Scope is part of the ask: requesting `own` and requesting `any` are two
    // different requests, and a widening between them shows nowhere else.
    if (r.scope) prev.scope = r.scope;
    out.set(key, prev);
  }
  return out;
}

function diffDlr(before, after) {
  const a = permissions(before);
  const b = permissions(after);
  const added = [];
  const removed = [];
  const changed = [];

  for (const [perm, now] of b) {
    if (!a.has(perm)) { added.push({ perm, ...now }); continue; }
    const was = a.get(perm);
    const gained = now.schemas.filter(s => !was.schemas.includes(s));
    const lost = was.schemas.filter(s => !now.schemas.includes(s));
    const scope = was.scope !== now.scope ? { was: was.scope, now: now.scope } : null;
    if (gained.length || lost.length || scope) changed.push({ perm, gained, lost, scope });
  }
  for (const [perm, was] of a) {
    if (!b.has(perm)) removed.push({ perm, ...was });
  }

  const parts = [];
  if (added.length) parts.push(`+${added.length} permission${added.length > 1 ? 's' : ''}`);
  if (removed.length) parts.push(`−${removed.length} permission${removed.length > 1 ? 's' : ''}`);
  if (changed.length) parts.push(`${changed.length} narrowed or widened`);

  if (!parts.length) {
    // The blob moved but no permission did. A version was still cut, so saying
    // "no spec fields changed" against a summary reading "Updated access
    // request" is the diff calling the developer a liar.
    //
    // Order-insensitively, because a round-trip through the editor reorders the
    // list and that is the one thing this must never report.
    if (canonicalLoose(before ?? null) === canonicalLoose(after ?? null)) return null;
    return {
      kind: 'dlr', field: 'data_lender_request', label: 'Access',
      headline: 'Request details changed — labels, ordering or plugin wiring. No permission moved.',
      added: [], removed: [], changed: [],
    };
  }
  return {
    kind: 'dlr', field: 'data_lender_request', label: 'Access',
    headline: parts.join(' · '), added, removed, changed,
  };
}

/**
 * Which sections were written, cleared or edited — not the prose itself.
 *
 * A CRUX rewrite is thousands of words, and a word-level diff of it is not a
 * thing anybody reads on a history tab. Which sections moved is, and it is the
 * shape the three scores are computed from in the first place.
 */
function diffCrux(before, after) {
  const a = (before && typeof before === 'object' && before.sections) || {};
  const b = (after && typeof after === 'object' && after.sections) || {};
  const keys = [...new Set([...Object.keys(a), ...Object.keys(b)])];

  const written = [];
  const cleared = [];
  const edited = [];
  for (const k of keys) {
    const wasBlank = isBlank(a[k]);
    const nowBlank = isBlank(b[k]);
    if (wasBlank && !nowBlank) written.push(sectionLabel(k));
    else if (!wasBlank && nowBlank) cleared.push(sectionLabel(k));
    else if (!wasBlank && !nowBlank && String(a[k]).trim() !== String(b[k]).trim()) {
      edited.push(sectionLabel(k));
    }
  }
  // The document itself can be swapped wholesale — pushed from a repo, or the
  // format migrated — with the section map coming out identical either side.
  const shell = !same({ ...before, sections: null }, { ...after, sections: null });
  if (!written.length && !cleared.length && !edited.length && !shell) return null;

  const filled = Object.values(b).filter(v => !isBlank(v)).length;
  const parts = [];
  if (written.length) parts.push(`+${written.length} written`);
  if (edited.length) parts.push(`${edited.length} rewritten`);
  if (cleared.length) parts.push(`−${cleared.length} cleared`);
  if (!parts.length && shell) parts.push('document replaced');
  return {
    kind: 'crux', field: 'crux', label: 'CRUX',
    headline: `${parts.join(' · ')} — ${filled} of ${CRUX_SECTIONS.length} sections written`,
    written, cleared, edited, filled, total: CRUX_SECTIONS.length,
  };
}

// ── everything else ─────────────────────────────────────────────────────────

const asList = v => (Array.isArray(v) ? v.filter(x => x != null).map(String) : null);

/* Fields the app record defaults on the way out but stores as null. The newest
 * change is diffed against the live app rather than a snapshot, and without
 * this every app that never set a deployment mode reports having just been
 * moved to incubator. */
const DEFAULTS = { deployment_mode: 'incubator', app_store_visibility: 'all_users' };

function diffField(field, before, after) {
  if (field in DEFAULTS) {
    before = before ?? DEFAULTS[field];
    after = after ?? DEFAULTS[field];
  }
  if (same(before, after)) return null;
  const label = FIELD_LABELS[field] || field.replace(/_/g, ' ');

  const wasList = asList(before);
  const nowList = asList(after);
  if (wasList && nowList) {
    const added = nowList.filter(x => !wasList.includes(x));
    const removed = wasList.filter(x => !nowList.includes(x));
    // Same members, different order. Not a change anyone made on purpose.
    if (!added.length && !removed.length) return null;
    const parts = [];
    if (added.length) parts.push(`+${added.length}`);
    if (removed.length) parts.push(`−${removed.length}`);
    return { kind: 'list', field, label, headline: parts.join(' · '), added, removed };
  }

  if (before !== null && after !== null
      && (typeof before === 'object' || typeof after === 'object')) {
    return { kind: 'json', field, label, headline: 'changed', was: before, now: after };
  }

  return { kind: 'scalar', field, label, was: before, now: after };
}

/* Row bookkeeping, not spec. These differ between every pair of versions and
 * mean nothing as a diff. */
const SKIP = new Set([
  'id', 'third_party_id', 'version', 'change_summary', 'changed_by',
  'changed_by_name', 'created_at', 'updated_at', 'spec_checks', 'spec_version',
  '_type',
]);

/**
 * Every field that moved between two snapshots, in reading order.
 *
 * `after` may be the next version row or the live app — both carry the same
 * field names, which is why the newest change is diffable at all.
 */
export function diffVersions(before, after) {
  if (!before || !after) return [];
  const out = [];
  const seen = new Set();

  for (const field of FIELD_ORDER) {
    seen.add(field);
    // Absent on one side is not "cleared". ThirdParty.to_dict only emits the
    // plugin fields for a plugin, so on the newest row — the only one diffed
    // against the live app — every ordinary app reported that its execution
    // type, requires_consent and remove_source had just been emptied.
    if (!(field in before) || !(field in after)) continue;
    const d = field === 'data_lender_request'
      ? diffDlr(before[field], after[field])
      : field === 'crux'
        ? diffCrux(before[field], after[field])
        : diffField(field, before[field] ?? null, after[field] ?? null);
    if (d) out.push(d);
  }

  // A field added to the snapshot but not to FIELD_ORDER still shows up, under
  // its column name. Invisible is worse than ugly on a history tab.
  //
  // Present on BOTH sides, which is what makes it a spec field rather than
  // something the app record computes on the way out. The newest change diffs
  // against the live app, whose to_dict carries client_id, is_verified, a
  // singular redirect_uri and a dozen others — every one of which would read as
  // a field that had just appeared.
  const extra = Object.keys(before)
    .filter(k => k in after && !seen.has(k) && !SKIP.has(k))
    .sort();
  for (const field of extra) {
    const d = diffField(field, before[field] ?? null, after[field] ?? null);
    if (d) out.push(d);
  }
  return out;
}

/* What kind of change it was, which is what the colour on the row encodes.
 *
 * Three, not one per field: the question you arrive at this tab with is "was
 * this a permissions change or a document change", and a hue per column would
 * answer a question nobody asks while making the ledger unreadable. */
export function changeKind(change) {
  if (change.field === 'crux') return 'crux';
  if (change.field === 'data_lender_request') return 'access';
  return 'meta';
}

/** The kind a whole change takes its colour from — the most consequential one
 *  it touched. Access outranks the document, which outranks a URL. */
export function eventKind(changes) {
  const kinds = new Set(changes.map(changeKind));
  return kinds.has('access') ? 'access' : kinds.has('crux') ? 'crux' : 'meta';
}

/** "2 fields changed", or the one field's own name when there is only one. */
export function summarise(changes) {
  if (!changes.length) return 'No spec fields changed';
  if (changes.length === 1) return `${changes[0].label} changed`;
  return `${changes.length} fields changed`;
}
